import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { customersAPI, exportAPI, quoteAPI, customersFilterAPI } from '../lib/api';
import { formatDate, formatSystemSize, formatCurrency, STATUS_DISPLAY_NAMES, STATUS_COLORS, PROJECT_STAGES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import {
  UserPlus,
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  Loader2,
  Users,
  MessageCircle,
  X,
  Download,
  FileText,
  ChevronRight,
  ArrowRight,
  Send,
  Mail,
  AlertTriangle,
  Archive,
  LayoutGrid,
  List,
  CheckSquare,
  UserCheck,
  User,
  Upload,
  ChevronDown,
  House,
  CornerDownRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { KanbanBoard } from '../components/KanbanBoard';
import { WhatsAppComposeDialog } from '../components/WhatsAppComposeDialog';
import { adminAPI } from '../lib/api';
import api from '../lib/api';
import { emailTemplatesAPI } from '../lib/api';
import { DuplicateBadge } from '../components/DuplicateBadge';
import { PaymentStatusBadge } from '../components/PaymentStatusBadge';
import { DnoStatusBadge } from '../components/DnoStatusBadge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { savedViewsAPI } from '../lib/api';
import { Save as SaveIcon, Bookmark } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Email trigger stages
const EMAIL_TRIGGER_STAGES = [
  'deposit_taken',
  'scheduled',
  'ordered',
  'in_progress_installation',
  'in_progress_invoiced',
  'in_progress_handover',
  'completed',
  'archived'
];

// Lightweight multi-select pill used by the advanced filter row.
const MultiSelectFilter = ({ label, options, value, onChange, testId, emptyHint }) => {
  const selectedCount = value.length;
  const toggle = (opt) => {
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  };
  const clear = (e) => { e.stopPropagation(); onChange([]); };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`h-8 text-xs ${selectedCount > 0 ? 'border-amber-400 bg-amber-50 text-amber-900' : 'border-slate-200'}`}
          data-testid={testId}
        >
          {label}
          {selectedCount > 0 && (
            <Badge className="ml-2 bg-amber-200 text-amber-900 hover:bg-amber-200 text-[10px] px-1.5 py-0 rounded-full">
              {selectedCount}
            </Badge>
          )}
          <ChevronDown className="w-3 h-3 ml-1.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="flex items-center justify-between mb-2 px-1">
          <span className="text-xs font-semibold text-slate-700">{label}</span>
          {selectedCount > 0 && (
            <button onClick={clear} className="text-xs text-slate-500 hover:text-slate-800" data-testid={`${testId}-clear`}>
              Clear
            </button>
          )}
        </div>
        {options.length === 0 ? (
          <p className="text-xs text-slate-400 px-1 py-3 text-center">{emptyHint}</p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {options.map((opt) => (
              <label
                key={opt}
                className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                data-testid={`${testId}-opt-${opt}`}
              >
                <Checkbox checked={value.includes(opt)} onCheckedChange={() => toggle(opt)} />
                <span className="text-xs capitalize text-slate-700">{opt}</span>
              </label>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

// Pre-saved quick filters for common workflow queues. Each applies a combo of
// the DNO / Deposit / Contract workflow filters (values match the Select
// options + backend params). Any workflow axis not listed resets to 'all'.
const CUSTOMER_QUICK_FILTERS = [
  {
    key: 'dno_outstanding',
    label: 'DNO Outstanding',
    hint: 'Signed + deposit paid + no DNO application raised',
    filters: { contract_status: 'fully_signed', deposit_status: 'paid', dno_status: 'not_raised' },
  },
  {
    key: 'outstanding_contract',
    label: 'Outstanding Contract',
    hint: 'Sent for signing but customer has not signed',
    filters: { contract_status: 'sent' },
  },
  {
    key: 'outstanding_deposit',
    label: 'Outstanding Deposit',
    hint: 'Signed but deposit not paid',
    filters: { contract_status: 'fully_signed', deposit_status: 'not_paid' },
  },
  {
    key: 'counter_sign',
    label: 'Counter Sign',
    hint: 'Customer signed — awaiting our signature',
    filters: { contract_status: 'customer_signed' },
  },
];


export const Customers = () => {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  // Multi-select advanced filters (installation type + inverter / battery brand).
  // Stored as comma-separated lists in URL params for shareable links.
  const [installTypeFilter, setInstallTypeFilter] = useState(() => {
    const v = searchParams.get('install_types') || '';
    return v ? v.split(',').filter(Boolean) : [];
  });
  const [inverterFilter, setInverterFilter] = useState(() => {
    const v = searchParams.get('inverters') || '';
    return v ? v.split(',').filter(Boolean) : [];
  });
  const [batteryFilter, setBatteryFilter] = useState(() => {
    const v = searchParams.get('batteries') || '';
    return v ? v.split(',').filter(Boolean) : [];
  });
  const [raisedByFilter, setRaisedByFilter] = useState(() => {
    const v = searchParams.get('raised_by') || '';
    return v ? v.split(',').filter(Boolean) : [];
  });
  // Workflow-stage filters: DNO / Deposit / Contract status. Each is a
  // single-value select that maps directly to the matching backend query
  // parameter. Empty string = "all".
  const [dnoStatusFilter, setDnoStatusFilter] = useState(searchParams.get('dno_status') || 'all');
  const [depositStatusFilter, setDepositStatusFilter] = useState(searchParams.get('deposit_status') || 'all');
  const [contractStatusFilter, setContractStatusFilter] = useState(searchParams.get('contract_status') || 'all');
  // Counts per filter option (e.g. how many customers match deposit=paid given
  // the current other-filter state). Refreshes whenever any filter changes.
  const [filterCounts, setFilterCounts] = useState({
    total: 0, grand_total: 0,
    dno: {}, deposit: {}, contract: {},
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, customer: null });
  const [archiveWarning, setArchiveWarning] = useState({ open: false, customer: null, newStatus: null });
  const [emailDialog, setEmailDialog] = useState({ 
    open: false, 
    customer: null, 
    newStatus: null,
    sendEmail: false,
    updating: false,
    templateId: null,
  });
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [templatePreview, setTemplatePreview] = useState({ subject: '', body: '' });
  const [viewMode, setViewMode] = useState('list');
  const [waCustomer, setWaCustomer] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [bulkEmailDialog, setBulkEmailDialog] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkSendEmail, setBulkSendEmail] = useState(false);
  const [bulkAssignUsers, setBulkAssignUsers] = useState([]);
  const [bulkEmailSubject, setBulkEmailSubject] = useState('');
  const [bulkEmailBody, setBulkEmailBody] = useState('');
  const [bulkEmailFrom, setBulkEmailFrom] = useState('default');
  const [bulkEmailTemplateId, setBulkEmailTemplateId] = useState('none');
  const [bulkEmailBccMode, setBulkEmailBccMode] = useState(true);
  const [bulkTemplates, setBulkTemplates] = useState([]);
  const [staffUsers, setStaffUsers] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  // Bulk Edit (curated single-field overwrite across many customers)
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkEditField, setBulkEditField] = useState('phone');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [bulkEditStaff, setBulkEditStaff] = useState([]);
  const [bulkEditBool, setBulkEditBool] = useState('true');
  const [bulkEditConfirm, setBulkEditConfirm] = useState(false);
  const [csvImportDialog, setCsvImportDialog] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const csvInputRef = React.useRef(null);
  const [duplicates, setDuplicates] = useState({});

  // Saved filter views (per-user)
  const [savedViews, setSavedViews] = useState([]);
  const [viewName, setViewName] = useState('');
  const [viewPopoverOpen, setViewPopoverOpen] = useState(false);

  const reloadSavedViews = React.useCallback(() => {
    savedViewsAPI.list('customers')
      .then((r) => setSavedViews(r.data || []))
      .catch(() => setSavedViews([]));
  }, []);

  useEffect(() => { reloadSavedViews(); }, [reloadSavedViews]);

  const captureCurrentView = () => ({
    search: searchQuery,
    status: statusFilter,
    install_types: installTypeFilter.join(','),
    inverters: inverterFilter.join(','),
    batteries: batteryFilter.join(','),
    dno_status: dnoStatusFilter,
    deposit_status: depositStatusFilter,
    contract_status: contractStatusFilter,
  });

  const saveCurrentView = async () => {
    const name = (viewName || '').trim();
    if (!name) { toast.error('Give this view a name'); return; }
    try {
      await savedViewsAPI.create({ name, context: 'customers', params: captureCurrentView() });
      toast.success(`Saved view "${name}"`);
      setViewName('');
      setViewPopoverOpen(false);
      reloadSavedViews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save view');
    }
  };

  const applyView = (v) => {
    const p = v.params || {};
    setSearchQuery(p.search || '');
    setStatusFilter(p.status || 'all');
    setInstallTypeFilter((p.install_types || '').split(',').filter(Boolean));
    setInverterFilter((p.inverters || '').split(',').filter(Boolean));
    setBatteryFilter((p.batteries || '').split(',').filter(Boolean));
    setDnoStatusFilter(p.dno_status || 'all');
    setDepositStatusFilter(p.deposit_status || 'all');
    setContractStatusFilter(p.contract_status || 'all');
    setViewPopoverOpen(false);
    toast.success(`Loaded "${v.name}"`);
  };

  const removeView = async (v) => {
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try { await savedViewsAPI.remove(v.id); reloadSavedViews(); toast.success('Removed'); }
    catch { toast.error('Could not remove'); }
  };

  const handleCsvDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/api/customers/import/template';
    link.download = 'helios_import_template.csv';
    link.click();
  };

  const handleCsvImport = async () => {
    if (!csvFile) return;
    setCsvImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await api.post('/customers/import/csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const data = res.data;
      toast.success(`${data.created} customers imported, ${data.skipped} skipped`);
      if (data.errors?.length > 0) {
        data.errors.forEach(e => toast.warning(e));
      }
      setCsvImportDialog(false);
      setCsvFile(null);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    } finally {
      setCsvImporting(false);
    }
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (dnoStatusFilter && dnoStatusFilter !== 'all') params.dno_status = dnoStatusFilter;
      if (depositStatusFilter && depositStatusFilter !== 'all') params.deposit_status = depositStatusFilter;
      if (contractStatusFilter && contractStatusFilter !== 'all') params.contract_status = contractStatusFilter;

      // Refresh customers + filter-option counts in parallel. Counts use the
      // SAME params so the numbers reflect the user's actual filter context.
      const [listRes, countsRes] = await Promise.all([
        customersAPI.getAll(params),
        customersFilterAPI.counts(params).catch(() => null),
      ]);
      setCustomers(listRes.data);
      if (countsRes?.data) setFilterCounts(countsRes.data);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  // Map staff user id -> display name, and helper to resolve who raised a customer.
  const staffMap = React.useMemo(() => {
    const m = {};
    for (const u of staffUsers) m[u.id] = u.name || u.email || u.id;
    return m;
  }, [staffUsers]);
  const raiserName = React.useCallback(
    (c) => staffMap[c.created_by] || c.created_by_name || '',
    [staffMap]
  );

  // Client-side advanced filters — applied on top of server-fetched results.
  const filteredCustomers = React.useMemo(() => {
    if (!installTypeFilter.length && !inverterFilter.length && !batteryFilter.length && !raisedByFilter.length) return customers;
    const norm = (v) => (v || '').toString().trim().toLowerCase();
    return customers.filter((c) => {
      if (installTypeFilter.length && !installTypeFilter.includes(norm(c.installation_type))) return false;
      if (inverterFilter.length && !inverterFilter.includes(norm(c.inverter_manufacturer))) return false;
      if (batteryFilter.length && !batteryFilter.includes(norm(c.battery_manufacturer))) return false;
      if (raisedByFilter.length && !raisedByFilter.includes(raiserName(c) || 'Unknown')) return false;
      return true;
    });
  }, [customers, installTypeFilter, inverterFilter, batteryFilter, raisedByFilter, raiserName]);

  // Group a person's jobs under one row (table view only). Headline = the
  // first/original job; each group keeps the full list of jobs for inline expand.
  const groupedRows = React.useMemo(() => {
    const groups = new Map();
    const firstIndex = new Map();
    filteredCustomers.forEach((c, i) => {
      const gid = c.job_group_id || c.id;
      if (!groups.has(gid)) { groups.set(gid, []); firstIndex.set(gid, i); }
      groups.get(gid).push(c);
    });
    const rows = [];
    for (const [gid, jobs] of groups) {
      const sorted = [...jobs].sort((a, b) => (a.created_at || '').localeCompare(b.created_at || ''));
      rows.push({ groupId: gid, headline: sorted[0], jobs: sorted });
    }
    rows.sort((a, b) => (firstIndex.get(a.groupId) ?? 0) - (firstIndex.get(b.groupId) ?? 0));
    return rows;
  }, [filteredCustomers]);

  const toggleGroupExpand = React.useCallback((gid) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  }, []);

  // Build the option lists for the dropdowns from actual customer data so the
  // user only sees brands/types they've actually used.
  const filterOptions = React.useMemo(() => {
    const inst = new Set();
    const inv = new Set();
    const bat = new Set();
    const raisers = new Set();
    for (const c of customers) {
      if (c.installation_type) inst.add(c.installation_type.toString().trim().toLowerCase());
      if (c.inverter_manufacturer) inv.add(c.inverter_manufacturer.toString().trim().toLowerCase());
      if (c.battery_manufacturer) bat.add(c.battery_manufacturer.toString().trim().toLowerCase());
      raisers.add(raiserName(c) || 'Unknown');
    }
    return {
      installTypes: [...inst].filter(Boolean).sort(),
      inverters: [...inv].filter(Boolean).sort(),
      batteries: [...bat].filter(Boolean).sort(),
      raisers: [...raisers].filter(Boolean).sort(),
    };
  }, [customers, raiserName]);

  // Sync the new filters into URL params
  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    if (installTypeFilter.length) params.install_types = installTypeFilter.join(',');
    else delete params.install_types;
    if (inverterFilter.length) params.inverters = inverterFilter.join(',');
    else delete params.inverters;
    if (batteryFilter.length) params.batteries = batteryFilter.join(',');
    else delete params.batteries;
    if (raisedByFilter.length) params.raised_by = raisedByFilter.join(',');
    else delete params.raised_by;
    if (dnoStatusFilter && dnoStatusFilter !== 'all') params.dno_status = dnoStatusFilter;
    else delete params.dno_status;
    if (depositStatusFilter && depositStatusFilter !== 'all') params.deposit_status = depositStatusFilter;
    else delete params.deposit_status;
    if (contractStatusFilter && contractStatusFilter !== 'all') params.contract_status = contractStatusFilter;
    else delete params.contract_status;
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [installTypeFilter, inverterFilter, batteryFilter, raisedByFilter, dnoStatusFilter, depositStatusFilter, contractStatusFilter]);
  useEffect(() => {
    fetchCustomers();
  }, [searchQuery, statusFilter, dnoStatusFilter, depositStatusFilter, contractStatusFilter]);

  useEffect(() => {
    if (customers.length > 0) {
      api.get('/duplicates/scan').then(res => {
        setDuplicates(res.data?.customers || {});
      }).catch(() => {});
    }
  }, [customers]);

  // Fetch staff users for bulk assign
  useEffect(() => {
    adminAPI.getUsers().then(res => setStaffUsers(res.data || [])).catch(() => {});
  }, []);

  // Fetch templates when email dialog opens
  useEffect(() => {
    if (emailDialog.open && emailDialog.newStatus) {
      setLoadingTemplates(true);
      emailTemplatesAPI.getAll().then(res => {
        const allTemplates = res.data || [];
        const relevant = allTemplates.filter(t =>
          t.trigger === emailDialog.newStatus || t.is_manual
        );
        setAvailableTemplates(relevant);
        // Auto-select the trigger-specific template if exists
        const triggerTemplate = relevant.find(t => t.trigger === emailDialog.newStatus && !t.is_default);
        if (triggerTemplate) {
          setEmailDialog(prev => ({ ...prev, templateId: triggerTemplate.id }));
        }
      }).catch(() => {
        setAvailableTemplates([]);
      }).finally(() => setLoadingTemplates(false));

      // Load default template preview for this trigger
      emailTemplatesAPI.getByTrigger(emailDialog.newStatus).then(res => {
        const t = res.data || {};
        setTemplatePreview({ subject: t.subject || '', body: t.body_html || '' });
      }).catch(() => {});
    }
  }, [emailDialog.open, emailDialog.newStatus]);

  // Reload preview when template selection changes
  useEffect(() => {
    if (!emailDialog.open || !emailDialog.sendEmail) return;
    const tid = emailDialog.templateId;
    if (!tid) {
      // Load default trigger template
      if (emailDialog.newStatus) {
        emailTemplatesAPI.getByTrigger(emailDialog.newStatus).then(res => {
          const t = res.data || {};
          setTemplatePreview({ subject: t.subject || '', body: t.body_html || '' });
        }).catch(() => {});
      }
    } else {
      const selected = availableTemplates.find(t => t.id === tid);
      if (selected) {
        setTemplatePreview({ subject: selected.subject || '', body: selected.body_html || '' });
      }
    }
  }, [emailDialog.templateId, emailDialog.sendEmail]);

  // NOTE: selections deliberately PERSIST across searches / filter changes so
  // users can tick records on one search, change the search, and keep adding
  // to the same selection without losing earlier picks.
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Ids of every record matching the CURRENT filters/search (the whole filtered
  // set, not just a visible page).
  const filteredIds = React.useMemo(() => filteredCustomers.map(c => c.id), [filteredCustomers]);
  const allFilteredSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.has(id));

  // Header checkbox: tick/untick every row in the current filtered view.
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });
  };

  // "Select all N matching" — add every filtered record to the selection.
  const selectAllMatching = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      filteredIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleBulkStatusChange = async () => {
    if (!bulkStatus) return;
    setBulkLoading(true);
    try {
      const res = await customersAPI.bulkStatus({
        customer_ids: Array.from(selectedIds),
        status: bulkStatus,
        send_email: bulkSendEmail,
      });
      toast.success(res.data.message);
      setBulkStatusDialog(false);
      setBulkStatus('');
      setBulkSendEmail(false);
      setSelectedIds(new Set());
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update statuses');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkAssign = async () => {
    setBulkLoading(true);
    try {
      const res = await customersAPI.bulkAssign({
        customer_ids: Array.from(selectedIds),
        assigned_user_ids: bulkAssignUsers,
      });
      toast.success(res.data.message);
      setBulkAssignDialog(false);
      setBulkAssignUsers([]);
      setSelectedIds(new Set());
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkEmail = async () => {
    if (!bulkEmailSubject || !bulkEmailBody) return;
    setBulkLoading(true);
    try {
      const fromValue = bulkEmailFrom === 'default'
        ? null
        : (bulkEmailFrom === 'user' ? (authUser?.email || null) : bulkEmailFrom);
      const res = await customersAPI.bulkEmail({
        customer_ids: Array.from(selectedIds),
        subject: bulkEmailSubject,
        body: bulkEmailBody,
        from_email: fromValue,
        template_id: bulkEmailTemplateId !== 'none' ? bulkEmailTemplateId : null,
        bcc_only: bulkEmailBccMode,
      });
      toast.success(res.data.message);
      setBulkEmailDialog(false);
      setBulkEmailSubject('');
      setBulkEmailBody('');
      setSelectedIds(new Set());
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setBulkLoading(false);
    }
  };

  // Curated fields the user may bulk-overwrite. `type` drives the input control.
  const BULK_EDIT_FIELD_OPTIONS = [
    { field: 'phone', label: 'Phone', type: 'text' },
    { field: 'email', label: 'Email', type: 'text' },
    { field: 'address', label: 'Address', type: 'text' },
    { field: 'city', label: 'City', type: 'text' },
    { field: 'postcode', label: 'Postcode', type: 'text' },
    { field: 'assigned_user_ids', label: 'Assigned staff', type: 'staff' },
    { field: 'referral_source', label: 'Lead source', type: 'text' },
    { field: 'property_type', label: 'Property type', type: 'property' },
    { field: 'portal_emails_enabled', label: 'Portal emails', type: 'bool' },
  ];

  const openBulkEdit = () => {
    setBulkEditField('phone');
    setBulkEditValue('');
    setBulkEditStaff([]);
    setBulkEditBool('true');
    setBulkEditConfirm(false);
    setBulkEditDialog(true);
  };

  const currentBulkEditType = BULK_EDIT_FIELD_OPTIONS.find(f => f.field === bulkEditField)?.type || 'text';
  const currentBulkEditLabel = BULK_EDIT_FIELD_OPTIONS.find(f => f.field === bulkEditField)?.label || bulkEditField;

  const bulkEditValueDisplay = () => {
    if (currentBulkEditType === 'staff') {
      const names = bulkEditStaff.map(id => staffMap[id]).filter(Boolean);
      return names.length ? names.join(', ') : 'None';
    }
    if (currentBulkEditType === 'bool') return bulkEditBool === 'true' ? 'On' : 'Off';
    return bulkEditValue.trim() || '(blank)';
  };

  const handleBulkEdit = async () => {
    setBulkLoading(true);
    try {
      let value;
      if (currentBulkEditType === 'staff') value = bulkEditStaff;
      else if (currentBulkEditType === 'bool') value = bulkEditBool === 'true';
      else value = bulkEditValue;
      const res = await customersAPI.bulkEdit({
        customer_ids: Array.from(selectedIds),
        field: bulkEditField,
        value,
      });
      toast.success(res.data.message);
      setBulkEditDialog(false);
      setBulkEditConfirm(false);
      setSelectedIds(new Set());
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to bulk edit');
    } finally {
      setBulkLoading(false);
    }
  };


  const applyBulkTemplate = (templateId) => {
    setBulkEmailTemplateId(templateId);
    if (templateId === 'none') {
      setBulkEmailSubject('');
      setBulkEmailBody('');
      return;
    }
    const t = bulkTemplates.find(x => x.id === templateId);
    if (t) {
      setBulkEmailSubject(t.subject || '');
      setBulkEmailBody(t.body_html || '');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (statusFilter !== 'all') params.set('status', statusFilter);
    setSearchParams(params);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    const params = new URLSearchParams(searchParams);
    if (value !== 'all') {
      params.set('status', value);
    } else {
      params.delete('status');
    }
    setSearchParams(params);
  };

  const handleDelete = async () => {
    if (!deleteDialog.customer) return;
    
    try {
      await customersAPI.delete(deleteDialog.customer.id);
      toast.success('Customer deleted successfully');
      setDeleteDialog({ open: false, customer: null });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDnoStatusFilter('all');
    setDepositStatusFilter('all');
    setContractStatusFilter('all');
    setInstallTypeFilter([]);
    setInverterFilter([]);
    setBatteryFilter([]);
    setSearchParams({});
  };

  // Apply a pre-saved quick filter: set the workflow axes it specifies and
  // reset the others to 'all'. Leaves status/equipment/search untouched so
  // presets compose with any active text search.
  const applyQuickFilter = (preset) => {
    const fl = preset.filters || {};
    setDnoStatusFilter(fl.dno_status || 'all');
    setDepositStatusFilter(fl.deposit_status || 'all');
    setContractStatusFilter(fl.contract_status || 'all');
  };

  // Which preset (if any) exactly matches the current workflow-filter state.
  const activeQuickFilter = CUSTOMER_QUICK_FILTERS.find((p) => {
    const fl = p.filters || {};
    return (
      (fl.dno_status || 'all') === dnoStatusFilter &&
      (fl.deposit_status || 'all') === depositStatusFilter &&
      (fl.contract_status || 'all') === contractStatusFilter
    );
  })?.key || null;


  const handleExport = async () => {
    try {
      // Pass the CURRENT filter state so the CSV reflects what the user is
      // looking at on screen (e.g. "Paid deposit + DNO submitted").
      const params = {};
      if (searchQuery) params.search = searchQuery;
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (dnoStatusFilter && dnoStatusFilter !== 'all') params.dno_status = dnoStatusFilter;
      if (depositStatusFilter && depositStatusFilter !== 'all') params.deposit_status = depositStatusFilter;
      if (contractStatusFilter && contractStatusFilter !== 'all') params.contract_status = contractStatusFilter;
      const response = await exportAPI.customersCSV(params);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded successfully');
    } catch (error) {
      toast.error('Failed to export customers');
    }
  };

  const handleQuickStatusChange = async (customerId, newStatus) => {
    const customer = customers.find(c => c.id === customerId);
    
    // Show archive/complete warning
    if (newStatus === 'archived' || newStatus === 'completed') {
      setArchiveWarning({ open: true, customer, newStatus });
      return;
    }
    
    // Check if this status triggers an email
    if (EMAIL_TRIGGER_STAGES.includes(newStatus)) {
      setEmailDialog({
        open: true,
        customer,
        newStatus,
        sendEmail: false,
        updating: false,
        templateId: null,
      });
    } else {
      // No email trigger, just update
      try {
        await customersAPI.updateStatus(customerId, { status: newStatus });
        toast.success(`Status updated to ${STATUS_DISPLAY_NAMES[newStatus]}`);
        fetchCustomers();
      } catch (error) {
        toast.error('Failed to update status');
      }
    }
  };

  const handleConfirmArchive = async () => {
    const { customer, newStatus } = archiveWarning;
    // Check if email trigger applies
    if (EMAIL_TRIGGER_STAGES.includes(newStatus)) {
      setArchiveWarning({ open: false, customer: null, newStatus: null });
      setEmailDialog({
        open: true,
        customer,
        newStatus,
        sendEmail: false,
        updating: false,
        templateId: null,
      });
    } else {
      try {
        await customersAPI.updateStatus(customer.id, { status: newStatus });
        toast.success(`Customer moved to ${STATUS_DISPLAY_NAMES[newStatus]}`);
        setArchiveWarning({ open: false, customer: null, newStatus: null });
        fetchCustomers();
      } catch (error) {
        toast.error('Failed to update status');
      }
    }
  };

  const handleConfirmStatusChange = async () => {
    setEmailDialog(prev => ({ ...prev, updating: true }));
    try {
      const payload = { 
        status: emailDialog.newStatus,
        send_email: emailDialog.sendEmail,
        template_id: emailDialog.templateId || null,
      };
      // If user edited the template content, pass overrides
      if (emailDialog.sendEmail && templatePreview.subject) {
        payload.template_subject = templatePreview.subject;
        payload.template_body = templatePreview.body;
      }
      await customersAPI.updateStatus(emailDialog.customer.id, payload);
      toast.success(
        emailDialog.sendEmail 
          ? `Status updated and email sent` 
          : `Status updated (no email sent)`
      );
      setEmailDialog({ open: false, customer: null, newStatus: null, sendEmail: false, updating: false, templateId: null });
      fetchCustomers();
    } catch (error) {
      toast.error('Failed to update status');
      setEmailDialog(prev => ({ ...prev, updating: false }));
    }
  };

  const handleDownloadQuote = async (customer) => {
    try {
      const response = await quoteAPI.generatePDF(customer.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Quote_${customer.first_name}_${customer.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Quote downloaded');
    } catch (error) {
      toast.error('Failed to generate quote');
    }
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all'
    || dnoStatusFilter !== 'all' || depositStatusFilter !== 'all' || contractStatusFilter !== 'all'
    || installTypeFilter.length || inverterFilter.length || batteryFilter.length;

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="customers-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']" data-testid="customers-heading">Customers</h1>
            <p className="text-slate-500 mt-1" data-testid="customers-count-line">
              {hasActiveFilters ? (
                <>
                  <span className="font-semibold text-slate-700">{groupedRows.length}</span>
                  {' '}customers shown
                </>
              ) : (
                <><span className="font-semibold text-slate-700">{groupedRows.length}</span> customers</>
              )}
              {filteredCustomers.length !== groupedRows.length && (
                <span className="ml-2 text-slate-400">· {filteredCustomers.length} jobs</span>
              )}
              {(filterCounts.grand_total || 0) > 0 && (
                <span className="ml-3 text-xs text-slate-400">
                  · Deposit paid: <span className="text-emerald-600 font-medium">{filterCounts.deposit?.paid ?? 0}</span>
                  {' · '}DNO submitted: <span className="text-blue-600 font-medium">{filterCounts.dno?.submitted ?? 0}</span>
                  {' · '}Fully signed: <span className="text-violet-600 font-medium">{filterCounts.contract?.fully_signed ?? 0}</span>
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-3">
            <div className="flex border border-slate-200 rounded-lg overflow-hidden" data-testid="view-toggle">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-none px-3 ${viewMode === 'list' ? 'bg-slate-900 text-white hover:bg-slate-800' : ''}`}
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'board' ? 'default' : 'ghost'}
                size="sm"
                className={`rounded-none px-3 ${viewMode === 'board' ? 'bg-slate-900 text-white hover:bg-slate-800' : ''}`}
                onClick={() => setViewMode('board')}
                data-testid="view-board-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            <Button 
              variant="outline" 
              onClick={handleExport}
              data-testid="export-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setCsvImportDialog(true)}
              data-testid="import-csv-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import CSV
            </Button>
            <Link to="/customers/new">
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-customer-btn">
                <UserPlus className="w-4 h-4 mr-2" />
                Add Customer
              </Button>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="p-4">
            {/* Pre-saved quick filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="quick-filters">
              <span className="text-xs font-medium text-slate-400 mr-1">Quick filters:</span>
              {CUSTOMER_QUICK_FILTERS.map((p) => {
                const active = activeQuickFilter === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    title={p.hint}
                    onClick={() => applyQuickFilter(p)}
                    className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`quick-filter-${p.key}`}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col md:flex-row gap-4">
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search by name, email, phone, or address..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="customer-search"
                  />
                </div>
              </form>
              <div className="flex gap-3 flex-wrap">
                <Select value={statusFilter} onValueChange={handleStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {PROJECT_STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>
                        {STATUS_DISPLAY_NAMES[stage]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={dnoStatusFilter} onValueChange={setDnoStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="dno-status-filter">
                    <SelectValue placeholder="DNO status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">DNO — All</SelectItem>
                    <SelectItem value="not_raised">Not raised ({filterCounts.dno?.not_raised ?? 0})</SelectItem>
                    <SelectItem value="submitted">Submitted ({filterCounts.dno?.submitted ?? 0})</SelectItem>
                    <SelectItem value="approved">Approved ({filterCounts.dno?.approved ?? 0})</SelectItem>
                    <SelectItem value="commissioned">Commissioned ({filterCounts.dno?.commissioned ?? 0})</SelectItem>
                    <SelectItem value="refused">Refused ({filterCounts.dno?.refused ?? 0})</SelectItem>
                    <SelectItem value="exempt">Exempt ({filterCounts.dno?.exempt ?? 0})</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={depositStatusFilter} onValueChange={setDepositStatusFilter}>
                  <SelectTrigger className="w-[180px]" data-testid="deposit-status-filter">
                    <SelectValue placeholder="Deposit status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Deposit — All</SelectItem>
                    <SelectItem value="paid">Paid ({filterCounts.deposit?.paid ?? 0})</SelectItem>
                    <SelectItem value="not_paid">Not paid ({filterCounts.deposit?.not_paid ?? 0})</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={contractStatusFilter} onValueChange={setContractStatusFilter}>
                  <SelectTrigger className="w-[190px]" data-testid="contract-status-filter">
                    <SelectValue placeholder="Contract status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Contract — All</SelectItem>
                    <SelectItem value="none">No contract yet ({filterCounts.contract?.none ?? 0})</SelectItem>
                    <SelectItem value="draft">Draft ({filterCounts.contract?.draft ?? 0})</SelectItem>
                    <SelectItem value="sent">Sent for signing ({filterCounts.contract?.sent ?? 0})</SelectItem>
                    <SelectItem value="customer_signed">Customer signed ({filterCounts.contract?.customer_signed ?? 0})</SelectItem>
                    <SelectItem value="fully_signed">Fully signed ({filterCounts.contract?.fully_signed ?? 0})</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters">
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Advanced multi-select filters */}
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100">
              <span className="text-xs uppercase tracking-wide text-slate-500 font-medium mr-1">Filter by:</span>
              <MultiSelectFilter
                label="System type"
                testId="install-type-filter"
                options={filterOptions.installTypes}
                value={installTypeFilter}
                onChange={setInstallTypeFilter}
                emptyHint="No installation types yet"
              />
              <MultiSelectFilter
                label="Inverter"
                testId="inverter-filter"
                options={filterOptions.inverters}
                value={inverterFilter}
                onChange={setInverterFilter}
                emptyHint="No inverter brands yet"
              />
              <MultiSelectFilter
                label="Battery"
                testId="battery-filter"
                options={filterOptions.batteries}
                value={batteryFilter}
                onChange={setBatteryFilter}
                emptyHint="No battery brands yet"
              />
              <MultiSelectFilter
                label="Raised by"
                testId="raised-by-filter"
                options={filterOptions.raisers}
                value={raisedByFilter}
                onChange={setRaisedByFilter}
                emptyHint="No staff yet"
              />
              {(installTypeFilter.length > 0 || inverterFilter.length > 0 || batteryFilter.length > 0 || raisedByFilter.length > 0) && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-xs h-7 text-slate-500 hover:text-slate-700"
                  onClick={() => { setInstallTypeFilter([]); setInverterFilter([]); setBatteryFilter([]); setRaisedByFilter([]); }}
                  data-testid="clear-advanced-filters"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear advanced
                </Button>
              )}
              <span className="text-xs text-slate-400 ml-auto">
                Showing {groupedRows.length} customer{groupedRows.length === 1 ? '' : 's'}{filteredCustomers.length !== groupedRows.length ? ` (${filteredCustomers.length} jobs)` : ''}
              </span>
            </div>

            {/* Saved Views — per-user filter snapshots */}
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-slate-100" data-testid="saved-views-bar">
              <span className="text-xs uppercase tracking-wide text-slate-500 font-medium mr-1 flex items-center gap-1">
                <Bookmark className="w-3.5 h-3.5" /> Saved views:
              </span>
              {savedViews.length === 0 && (
                <span className="text-[11px] text-slate-400 italic">None yet — save the current filter for quick access later.</span>
              )}
              {savedViews.map((v) => (
                <div key={v.id} className="inline-flex items-center gap-1 rounded-full bg-blue-50 border border-blue-200 px-2 py-0.5">
                  <button
                    className="text-[11px] font-medium text-blue-700 hover:underline"
                    onClick={() => applyView(v)}
                    data-testid={`saved-view-${v.id}`}
                  >
                    {v.name}
                  </button>
                  <button
                    className="text-[10px] text-slate-400 hover:text-rose-600"
                    onClick={() => removeView(v)}
                    title="Delete view"
                    data-testid={`saved-view-remove-${v.id}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <Popover open={viewPopoverOpen} onOpenChange={setViewPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600 hover:text-blue-700" data-testid="save-view-trigger">
                    <SaveIcon className="w-3.5 h-3.5 mr-1" /> Save current
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="start">
                  <p className="text-xs font-medium text-slate-700 mb-2">Name this view</p>
                  <Input
                    value={viewName}
                    onChange={(e) => setViewName(e.target.value)}
                    placeholder="e.g. Stale leads (30d+)"
                    className="mb-2"
                    onKeyDown={(e) => e.key === 'Enter' && saveCurrentView()}
                    data-testid="save-view-name"
                  />
                  <Button size="sm" className="w-full bg-blue-600 hover:bg-blue-700 text-white" onClick={saveCurrentView} data-testid="save-view-confirm">
                    <SaveIcon className="w-3.5 h-3.5 mr-1" /> Save
                  </Button>
                  <p className="text-[10px] text-slate-400 mt-2">Captures search, status, system type, inverter and battery filters.</p>
                </PopoverContent>
              </Popover>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg animate-fade-in" data-testid="bulk-actions-bar">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900" data-testid="bulk-selected-count">{selectedIds.size} selected</span>
              {!allFilteredSelected && filteredIds.length > 0 && (
                <button
                  onClick={selectAllMatching}
                  className="text-xs font-medium text-blue-600 underline hover:text-blue-800"
                  data-testid="select-all-matching-btn"
                >
                  Select all {filteredIds.length} matching
                </button>
              )}
            </div>
            <div className="h-5 w-px bg-blue-200" />
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100" onClick={() => { setBulkStatus(''); setBulkSendEmail(false); setBulkStatusDialog(true); }} data-testid="bulk-status-btn">
              <ArrowRight className="w-3.5 h-3.5 mr-1.5" /> Change Status
            </Button>
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100" onClick={() => { setBulkAssignUsers([]); setBulkAssignDialog(true); }} data-testid="bulk-assign-btn">
              <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Assign
            </Button>
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100" onClick={openBulkEdit} data-testid="bulk-edit-btn">
              <Edit className="w-3.5 h-3.5 mr-1.5" /> Bulk Edit
            </Button>
            <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100" onClick={() => {
              setBulkEmailSubject('');
              setBulkEmailBody('');
              setBulkEmailFrom('default');
              setBulkEmailTemplateId('none');
              setBulkEmailBccMode(true);
              // Load templates
              emailTemplatesAPI.getAll().then(r => setBulkTemplates(r.data || [])).catch(() => setBulkTemplates([]));
              setBulkEmailDialog(true);
            }} data-testid="bulk-email-btn">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> Send Email
            </Button>
            <div className="ml-auto">
              <Button size="sm" variant="ghost" className="text-blue-600" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
                <X className="w-3.5 h-3.5 mr-1" /> Clear
              </Button>
            </div>
          </div>
        )}

        {/* Customers Content */}
        {viewMode === 'board' ? (
          <div>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : (
              <KanbanBoard
                customers={customers}
                onStatusChange={handleQuickStatusChange}
              />
            )}
          </div>
        ) : (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Users className="w-16 h-16 mb-4" />
                <p className="text-lg font-medium">No customers found</p>
                <p className="text-sm mt-1">
                  {hasActiveFilters
                    ? 'Try adjusting your filters'
                    : 'Add your first customer to get started'}
                </p>
                {!hasActiveFilters && (
                  <Link to="/customers/new" className="mt-4">
                    <Button className="bg-amber-500 hover:bg-amber-600 text-white">
                      <UserPlus className="w-4 h-4 mr-2" />
                      Add Customer
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={allFilteredSelected}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-checkbox"
                        />
                      </TableHead>
                      <TableHead className="font-semibold">Name</TableHead>
                      <TableHead className="font-semibold">Contact</TableHead>
                      <TableHead className="font-semibold">Property Type</TableHead>
                      <TableHead className="font-semibold">System Size</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold">Created</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {groupedRows.map((group) => {
                      const customer = group.headline;
                      const jobCount = group.jobs.length;
                      const isExpanded = expandedGroups.has(group.groupId);
                      const groupJobIds = group.jobs.map((j) => j.id);
                      const allGroupSelected = groupJobIds.every((jid) => selectedIds.has(jid));
                      return (
                      <React.Fragment key={group.groupId}>
                      <TableRow
                        key={customer.id}
                        className={`table-row-hover cursor-pointer ${selectedIds.has(customer.id) ? 'bg-blue-50' : ''}`}
                        onClick={() => navigate(`/customers/${customer.id}`)}
                        data-testid={`customer-row-${customer.id}`}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={jobCount > 1 ? allGroupSelected : selectedIds.has(customer.id)}
                            onCheckedChange={() => {
                              if (jobCount > 1) {
                                setSelectedIds((prev) => {
                                  const next = new Set(prev);
                                  if (allGroupSelected) groupJobIds.forEach((jid) => next.delete(jid));
                                  else groupJobIds.forEach((jid) => next.add(jid));
                                  return next;
                                });
                              } else {
                                toggleSelect(customer.id);
                              }
                            }}
                            data-testid={`select-customer-${customer.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-slate-900 flex items-center gap-2">
                              {customer.first_name} {customer.last_name}
                              {jobCount > 1 && (
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); toggleGroupExpand(group.groupId); }}
                                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-medium hover:bg-amber-200 transition-colors"
                                  data-testid={`jobs-badge-${group.groupId}`}
                                  title="This customer has multiple jobs — click to expand"
                                >
                                  <House className="w-3 h-3" />
                                  {jobCount} jobs
                                  <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
                              )}
                            </p>
                            <p className="text-sm text-slate-500 truncate max-w-[200px]">
                              {customer.address}
                            </p>
                            {raiserName(customer) && (
                              <p className="text-xs text-slate-400 mt-0.5" data-testid={`customer-raised-by-${customer.id}`}>
                                <User className="w-3 h-3 inline -mt-0.5 mr-1" />
                                Raised by {raiserName(customer)}
                              </p>
                            )}
                            {duplicates[customer.id] && (
                              <DuplicateBadge
                                matches={duplicates[customer.id]}
                                currentSource="customer"
                                currentId={customer.id}
                                onMerged={fetchCustomers}
                                compact
                              />
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm text-slate-900">{customer.email}</p>
                            <p className="text-sm text-slate-500">{customer.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="capitalize text-slate-700">{customer.property_type}</span>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-slate-900">
                            {formatSystemSize(customer.system_size)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <Badge className={`${STATUS_COLORS[customer.status]} border`}>
                              {STATUS_DISPLAY_NAMES[customer.status]}
                            </Badge>
                            <PaymentStatusBadge customer={customer} variant="compact" />
                            <DnoStatusBadge customer={customer} />
                            {customer.portal_emails_enabled === false && (
                              <Badge
                                className="bg-slate-100 text-slate-500 border border-slate-200 text-[10px]"
                                title="Customer Portal emails disabled — no automated welcome or status updates will be sent"
                                data-testid={`portal-off-${customer.id}`}
                              >
                                Portal: OFF
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">
                            {formatDate(customer.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                          {customer.phone && (
                            <Button
                              variant="ghost" size="icon"
                              className="text-emerald-600 hover:bg-emerald-50"
                              title="Message on WhatsApp"
                              data-testid={`customer-whatsapp-${customer.id}`}
                              onClick={(e) => { e.stopPropagation(); setWaCustomer(customer); }}
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" data-testid={`customer-menu-${customer.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customers/${customer.id}`);
                              }}>
                                <Eye className="w-4 h-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customers/${customer.id}/edit`);
                              }}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuSub>
                                <DropdownMenuSubTrigger onClick={(e) => e.stopPropagation()}>
                                  <ArrowRight className="w-4 h-4 mr-2" />
                                  Change Status
                                </DropdownMenuSubTrigger>
                                <DropdownMenuPortal>
                                  <DropdownMenuSubContent>
                                    {PROJECT_STAGES.map((stage) => (
                                      <DropdownMenuItem
                                        key={stage}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleQuickStatusChange(customer.id, stage);
                                        }}
                                        className={customer.status === stage ? 'bg-blue-50' : ''}
                                      >
                                        <span className={`w-2 h-2 rounded-full mr-2 ${STATUS_COLORS[stage]?.split(' ')[0] || 'bg-gray-200'}`} />
                                        {STATUS_DISPLAY_NAMES[stage]}
                                        {customer.status === stage && <span className="ml-auto text-xs text-blue-600">Current</span>}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuPortal>
                              </DropdownMenuSub>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadQuote(customer);
                              }}>
                                <FileText className="w-4 h-4 mr-2" />
                                Download Quote PDF
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteDialog({ open: true, customer });
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                      {isExpanded && jobCount > 1 && group.jobs.map((job, ji) => (
                        <TableRow
                          key={`${group.groupId}-job-${job.id}`}
                          className="bg-slate-50/70 cursor-pointer hover:bg-slate-100"
                          onClick={() => navigate(`/customers/${job.id}`)}
                          data-testid={`job-subrow-${job.id}`}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={selectedIds.has(job.id)}
                              onCheckedChange={() => toggleSelect(job.id)}
                              data-testid={`select-customer-${job.id}`}
                            />
                          </TableCell>
                          <TableCell colSpan={5}>
                            <div className="flex items-center gap-2 pl-6">
                              <CornerDownRight className="w-4 h-4 text-slate-400" />
                              <span className="font-medium text-slate-700">
                                {job.job_label || `Job ${ji + 1}`}
                              </span>
                              <Badge className={`${STATUS_COLORS[job.status]} border`}>
                                {STATUS_DISPLAY_NAMES[job.status]}
                              </Badge>
                              <PaymentStatusBadge customer={job} variant="compact" />
                              {job.system_size ? (
                                <span className="text-sm text-slate-500 ml-1">{formatSystemSize(job.system_size)}</span>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-500">{formatDate(job.created_at)}</span>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" onClick={() => navigate(`/customers/${job.id}`)} data-testid={`open-job-${job.id}`} title="Open this job">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, customer: null })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Customer</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete{' '}
                <span className="font-medium">
                  {deleteDialog.customer?.first_name} {deleteDialog.customer?.last_name}
                </span>
                ? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Email Confirmation Dialog */}
        <Dialog 
          open={emailDialog.open} 
          onOpenChange={(open) => !emailDialog.updating && setEmailDialog({ open, customer: null, newStatus: null, sendEmail: false, updating: false, templateId: null })}
        >
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" />
                Status Change — {STATUS_DISPLAY_NAMES[emailDialog.newStatus]}
              </DialogTitle>
              <DialogDescription>
                Moving <span className="font-medium">{emailDialog.customer?.first_name} {emailDialog.customer?.last_name}</span> to{' '}
                <span className="font-medium">{STATUS_DISPLAY_NAMES[emailDialog.newStatus]}</span>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="send-email"
                  checked={emailDialog.sendEmail}
                  onCheckedChange={(checked) => setEmailDialog(prev => ({ ...prev, sendEmail: checked }))}
                  data-testid="send-email-checkbox"
                />
                <Label htmlFor="send-email" className="cursor-pointer font-medium">
                  Send email notification to customer
                </Label>
              </div>
              
              {emailDialog.sendEmail && (
                <div className="space-y-3 border-t pt-3">
                  <div className="space-y-2">
                    <Label>Email Template</Label>
                    {loadingTemplates ? (
                      <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading templates...
                      </div>
                    ) : (
                      <Select
                        value={emailDialog.templateId || 'default'}
                        onValueChange={(val) => setEmailDialog(prev => ({ ...prev, templateId: val === 'default' ? null : val }))}
                      >
                        <SelectTrigger data-testid="template-select">
                          <SelectValue placeholder="Select email template..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="default">
                            Default Template ({STATUS_DISPLAY_NAMES[emailDialog.newStatus]})
                          </SelectItem>
                          {availableTemplates.filter(t => !t.is_default).map(t => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.is_manual ? `[Custom] ` : ''}{t.label || t.subject || t.trigger}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>

                  {/* Template Preview */}
                  {templatePreview.subject && (
                    <div className="space-y-2">
                      <Label className="text-sm text-slate-600">Subject</Label>
                      <Input
                        value={templatePreview.subject}
                        onChange={e => setTemplatePreview(prev => ({ ...prev, subject: e.target.value }))}
                        data-testid="template-subject-edit"
                      />
                      <Label className="text-sm text-slate-600">Body (HTML)</Label>
                      <textarea
                        className="w-full min-h-[120px] text-sm font-mono p-3 border rounded-lg bg-slate-50 resize-y"
                        value={templatePreview.body}
                        onChange={e => setTemplatePreview(prev => ({ ...prev, body: e.target.value }))}
                        data-testid="template-body-edit"
                      />
                      <p className="text-xs text-slate-400">
                        Variables like {'{{first_name}}'}, {'{{portal_url}}'}, {'{{setup_url}}'} will be replaced with customer data.
                      </p>
                    </div>
                  )}

                  <div className="p-3 bg-slate-50 rounded-lg text-sm">
                    <p className="text-slate-600">
                      Email will be sent to: <span className="font-medium">{emailDialog.customer?.email}</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setEmailDialog({ open: false, customer: null, newStatus: null, sendEmail: false, updating: false, templateId: null })}
                disabled={emailDialog.updating}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmStatusChange}
                disabled={emailDialog.updating}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="confirm-status-email"
              >
                {emailDialog.updating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : emailDialog.sendEmail ? (
                  <Send className="w-4 h-4 mr-2" />
                ) : null}
                {emailDialog.sendEmail ? 'Update & Send Email' : 'Update Status'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Archive/Complete Warning Dialog */}
        <Dialog open={archiveWarning.open} onOpenChange={(open) => !open && setArchiveWarning({ open: false, customer: null, newStatus: null })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {archiveWarning.newStatus === 'archived' ? 'Archive Customer' : 'Complete Customer'}
              </DialogTitle>
              <DialogDescription>
                {archiveWarning.newStatus === 'archived'
                  ? <>This will move <span className="font-medium">{archiveWarning.customer?.first_name} {archiveWarning.customer?.last_name}</span> to <span className="font-semibold text-slate-700">Archive/Lost</span>. The customer will be removed from the active pipeline and moved to the Archive.</>
                  : <>This will mark <span className="font-medium">{archiveWarning.customer?.first_name} {archiveWarning.customer?.last_name}</span> as <span className="font-semibold text-emerald-700">Completed</span>. The customer will be removed from the active pipeline and moved to the Archive.</>
                }
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className={`p-4 rounded-lg border ${archiveWarning.newStatus === 'archived' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${archiveWarning.newStatus === 'archived' ? 'text-red-500' : 'text-amber-500'}`} />
                  <div className="text-sm">
                    <p className={archiveWarning.newStatus === 'archived' ? 'text-red-800' : 'text-amber-800'}>
                      {archiveWarning.newStatus === 'archived'
                        ? 'This customer will no longer appear in the main customers list. They can be viewed in the Archive section under Customers.'
                        : 'This customer will no longer appear in the main customers list. They can be viewed in the Archive section under Customers.'
                      }
                    </p>
                    {archiveWarning.customer?.sharepoint_folder_id && archiveWarning.newStatus === 'archived' && (
                      <p className="mt-2 text-red-700 font-medium">
                        Their SharePoint folder will be moved to the Archive folder.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setArchiveWarning({ open: false, customer: null, newStatus: null })}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmArchive}
                className={archiveWarning.newStatus === 'archived' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                data-testid="confirm-archive"
              >
                <Archive className="w-4 h-4 mr-2" />
                {archiveWarning.newStatus === 'archived' ? 'Move to Archive' : 'Mark Complete & Archive'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Status Change Dialog */}
        <Dialog open={bulkStatusDialog} onOpenChange={(open) => !bulkLoading && setBulkStatusDialog(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowRight className="w-5 h-5" /> Bulk Status Change
              </DialogTitle>
              <DialogDescription>Change status for {selectedIds.size} selected customer(s)</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>New Status</Label>
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger data-testid="bulk-status-select">
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    {PROJECT_STAGES.map((stage) => (
                      <SelectItem key={stage} value={stage}>{STATUS_DISPLAY_NAMES[stage]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {bulkStatus && EMAIL_TRIGGER_STAGES.includes(bulkStatus) && (
                <div className="flex items-center space-x-3">
                  <Checkbox id="bulk-send-email" checked={bulkSendEmail} onCheckedChange={setBulkSendEmail} data-testid="bulk-send-email-check" />
                  <Label htmlFor="bulk-send-email">Send notification email to customers</Label>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkStatusDialog(false)} disabled={bulkLoading}>Cancel</Button>
              <Button onClick={handleBulkStatusChange} disabled={bulkLoading || !bulkStatus} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-status-confirm">
                {bulkLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Update {selectedIds.size} Customers
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Assign Dialog */}
        <Dialog open={bulkAssignDialog} onOpenChange={(open) => !bulkLoading && setBulkAssignDialog(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5" /> Bulk Assign
              </DialogTitle>
              <DialogDescription>Assign staff to {selectedIds.size} selected customer(s)</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2 max-h-64 overflow-y-auto">
              {staffUsers.map(u => (
                <div key={u.id} className="flex items-center space-x-3">
                  <Checkbox
                    id={`assign-${u.id}`}
                    checked={bulkAssignUsers.includes(u.id)}
                    onCheckedChange={(checked) => {
                      setBulkAssignUsers(prev => checked ? [...prev, u.id] : prev.filter(id => id !== u.id));
                    }}
                    data-testid={`assign-user-${u.id}`}
                  />
                  <Label htmlFor={`assign-${u.id}`} className="flex items-center gap-2 cursor-pointer">
                    <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold">
                      {u.name.charAt(0).toUpperCase()}
                    </div>
                    {u.name}
                    <span className="text-xs text-slate-400">{u.email}</span>
                  </Label>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkAssignDialog(false)} disabled={bulkLoading}>Cancel</Button>
              <Button onClick={handleBulkAssign} disabled={bulkLoading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-assign-confirm">
                {bulkLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Assign to {bulkAssignUsers.length} Staff
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Dialog — overwrite one curated field across selected customers */}
        <Dialog open={bulkEditDialog} onOpenChange={(open) => !bulkLoading && setBulkEditDialog(open)}>
          <DialogContent data-testid="bulk-edit-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" /> Bulk Edit
              </DialogTitle>
              <DialogDescription>
                Overwrite a single field on {selectedIds.size} selected customer{selectedIds.size === 1 ? '' : 's'}.
              </DialogDescription>
            </DialogHeader>

            {!bulkEditConfirm ? (
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-sm font-medium">Field to update</Label>
                  <Select
                    value={bulkEditField}
                    onValueChange={(v) => { setBulkEditField(v); setBulkEditValue(''); setBulkEditStaff([]); setBulkEditBool('true'); }}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="bulk-edit-field-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BULK_EDIT_FIELD_OPTIONS.map(f => (
                        <SelectItem key={f.field} value={f.field}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium">New value</Label>
                  {currentBulkEditType === 'text' && (
                    <Input
                      className="mt-1.5"
                      value={bulkEditValue}
                      onChange={(e) => setBulkEditValue(e.target.value)}
                      placeholder={`Enter new ${currentBulkEditLabel.toLowerCase()}`}
                      data-testid="bulk-edit-value-input"
                    />
                  )}
                  {currentBulkEditType === 'property' && (
                    <Select value={bulkEditValue} onValueChange={setBulkEditValue}>
                      <SelectTrigger className="mt-1.5" data-testid="bulk-edit-property-select">
                        <SelectValue placeholder="Select property type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="residential">Residential</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {currentBulkEditType === 'bool' && (
                    <Select value={bulkEditBool} onValueChange={setBulkEditBool}>
                      <SelectTrigger className="mt-1.5" data-testid="bulk-edit-bool-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">On — send portal emails</SelectItem>
                        <SelectItem value="false">Off — no portal emails</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {currentBulkEditType === 'staff' && (
                    <div className="mt-1.5 space-y-2 max-h-52 overflow-y-auto border rounded-md p-3">
                      {staffUsers.map(u => (
                        <div key={u.id} className="flex items-center space-x-3">
                          <Checkbox
                            id={`bulk-edit-staff-${u.id}`}
                            checked={bulkEditStaff.includes(u.id)}
                            onCheckedChange={(checked) => {
                              setBulkEditStaff(prev => checked ? [...prev, u.id] : prev.filter(id => id !== u.id));
                            }}
                            data-testid={`bulk-edit-staff-${u.id}`}
                          />
                          <Label htmlFor={`bulk-edit-staff-${u.id}`} className="flex items-center gap-2 cursor-pointer">
                            <div className="w-7 h-7 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-semibold">
                              {u.name.charAt(0).toUpperCase()}
                            </div>
                            {u.name}
                            <span className="text-xs text-slate-400">{u.email}</span>
                          </Label>
                        </div>
                      ))}
                      <p className="text-xs text-slate-400">Selecting none will clear all assigned staff.</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="py-4" data-testid="bulk-edit-confirm-panel">
                <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-900">
                    This will overwrite <strong>{currentBulkEditLabel}</strong> on{' '}
                    <strong>{selectedIds.size} customer{selectedIds.size === 1 ? '' : 's'}</strong> with{' '}
                    <strong>"{bulkEditValueDisplay()}"</strong>. This cannot be undone.
                  </div>
                </div>
              </div>
            )}

            <DialogFooter>
              {!bulkEditConfirm ? (
                <>
                  <Button variant="outline" onClick={() => setBulkEditDialog(false)} disabled={bulkLoading}>Cancel</Button>
                  <Button
                    onClick={() => setBulkEditConfirm(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="bulk-edit-review-btn"
                  >
                    Review change
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setBulkEditConfirm(false)} disabled={bulkLoading}>Back</Button>
                  <Button
                    onClick={handleBulkEdit}
                    disabled={bulkLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="bulk-edit-confirm-btn"
                  >
                    {bulkLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Overwrite {selectedIds.size} record{selectedIds.size === 1 ? '' : 's'}
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Bulk Email Dialog */}
        <Dialog open={bulkEmailDialog} onOpenChange={(open) => !bulkLoading && setBulkEmailDialog(open)}>
          <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" /> Bulk Email
              </DialogTitle>
              <DialogDescription>
                Send email to {selectedIds.size} selected customer(s).
                {bulkEmailBccMode
                  ? ' All recipients will be BCC\'d (GDPR-compliant — emails stay private).'
                  : ' Personalised: one email per customer using {{first_name}}, {{last_name}}, {{name}}, {{email}}.'}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {/* Top controls: Template + From + Mode */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">Template</Label>
                  <Select value={bulkEmailTemplateId} onValueChange={applyBulkTemplate}>
                    <SelectTrigger data-testid="bulk-email-template-select">
                      <SelectValue placeholder="Select a template..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template (custom)</SelectItem>
                      {bulkTemplates.filter(t => t.active !== false).map(t => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name || t.subject || t.trigger}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-slate-600">From mailbox</Label>
                  <Select value={bulkEmailFrom} onValueChange={setBulkEmailFrom}>
                    <SelectTrigger data-testid="bulk-email-from-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default mailbox</SelectItem>
                      <SelectItem value="hello@helioses.co.uk">hello@helioses.co.uk</SelectItem>
                      <SelectItem value="noreply@helioses.co.uk">noreply@helioses.co.uk</SelectItem>
                      {authUser?.email && (
                        <SelectItem value="user">My mailbox ({authUser.email})</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded">
                <Checkbox
                  id="bulk-bcc-mode"
                  checked={bulkEmailBccMode}
                  onCheckedChange={(v) => setBulkEmailBccMode(!!v)}
                  data-testid="bulk-email-bcc-toggle"
                />
                <label htmlFor="bulk-bcc-mode" className="text-sm cursor-pointer flex-1">
                  <strong>BCC mode (GDPR)</strong> — single email with all customers as BCC.
                  Uncheck to send personalised emails (one per customer with placeholders).
                </label>
              </div>

              <div className="space-y-2">
                <Label>Subject</Label>
                <Input value={bulkEmailSubject} onChange={e => setBulkEmailSubject(e.target.value)} placeholder="e.g. Important update from Helios" data-testid="bulk-email-subject" />
              </div>

              {/* Live editor: split view */}
              <div className="space-y-2">
                <Label>Body (live preview as you type)</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-[260px]">
                  <div className="flex flex-col">
                    <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Edit (HTML)</div>
                    <Textarea
                      value={bulkEmailBody}
                      onChange={e => setBulkEmailBody(e.target.value)}
                      placeholder={bulkEmailBccMode
                        ? "<p>Dear customer,</p>\n<p>We wanted to let you know...</p>"
                        : "<p>Dear {{first_name}},</p>\n<p>We wanted to let you know...</p>"}
                      rows={12}
                      className="font-mono text-xs flex-1 resize-none"
                      data-testid="bulk-email-body"
                    />
                  </div>
                  <div className="flex flex-col">
                    <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500 mb-1">Live preview</div>
                    <div
                      className="border border-slate-200 rounded p-3 bg-white text-sm overflow-y-auto flex-1"
                      style={{ minHeight: '260px' }}
                      data-testid="bulk-email-preview"
                      dangerouslySetInnerHTML={{ __html: bulkEmailBody || '<span class="text-slate-300">Body preview…</span>' }}
                    />
                  </div>
                </div>
                {!bulkEmailBccMode && (
                  <p className="text-[11px] text-slate-500">
                    Personalisation variables: <code>{'{{first_name}}'}</code>, <code>{'{{last_name}}'}</code>, <code>{'{{name}}'}</code>, <code>{'{{email}}'}</code>
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkEmailDialog(false)} disabled={bulkLoading}>Cancel</Button>
              <Button onClick={handleBulkEmail} disabled={bulkLoading || !bulkEmailSubject || !bulkEmailBody} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="bulk-email-confirm">
                {bulkLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {bulkEmailBccMode ? `Send (BCC ${selectedIds.size})` : `Send to ${selectedIds.size} Customers`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* CSV Import Dialog */}
        <Dialog open={csvImportDialog} onOpenChange={(open) => !csvImporting && setCsvImportDialog(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" /> Import Customers from CSV
              </DialogTitle>
              <DialogDescription>
                Upload a CSV file to bulk import customers. Download the template first to see the required format.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <Button variant="outline" onClick={handleCsvDownloadTemplate} className="w-full" data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-2" /> Download CSV Template
              </Button>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
                  data-testid="csv-file-input"
                />
                {csvFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <FileText className="w-8 h-8 text-blue-500" />
                    <div className="text-left">
                      <p className="font-medium text-slate-900">{csvFile.name}</p>
                      <p className="text-xs text-slate-500">{(csvFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => { setCsvFile(null); if(csvInputRef.current) csvInputRef.current.value=''; }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <Button variant="outline" onClick={() => csvInputRef.current?.click()} data-testid="choose-csv-btn">
                      Choose CSV File
                    </Button>
                    <p className="text-xs text-slate-400 mt-2">CSV files only</p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setCsvImportDialog(false); setCsvFile(null); }} disabled={csvImporting}>Cancel</Button>
              <Button onClick={handleCsvImport} disabled={csvImporting || !csvFile} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="import-csv-confirm">
                {csvImporting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Import Customers
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <WhatsAppComposeDialog
          open={!!waCustomer}
          onOpenChange={(o) => { if (!o) setWaCustomer(null); }}
          phone={waCustomer?.whatsapp_number || waCustomer?.phone || ''}
          customerId={waCustomer?.id}
          customerName={waCustomer ? `${waCustomer.first_name || ''} ${waCustomer.last_name || ''}`.trim() : ''}
        />
      </div>
    </Layout>
  );
};
